import React, { useEffect, useMemo, useState } from 'react'
import { useUnit } from 'effector-react'
import dayjs from 'dayjs'
import { useKPIList } from './hooks'
import { CCardBody } from '@coreui/react-pro'
import {
  Card,
  Flex,
  Progress,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { CardVerification } from './types'
import { CardVerificationFilters } from '../atoms/CardVerificationFilters'
import { $userProfile } from 'src/features/user'
import { rolesObj } from 'src/libs/role'
import { RangePickerProps } from 'antd/es/date-picker'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileProtectOutlined,
  TeamOutlined,
} from '@ant-design/icons'

const { Text } = Typography

const pctCell = (val: number) => {
  const color = val >= 14 ? '#22c55e' : val >= 8 ? '#f59e0b' : '#ef4444'
  return (
    <Flex align="center" gap={10}>
      <span
        style={{
          fontFamily: 'monospace',
          width: 72,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {val.toFixed(2)}%
      </span>
      <Progress
        percent={val}
        showInfo={false}
        strokeColor={color}
        trailColor="rgba(128,128,128,0.15)"
        style={{ flex: 1, minWidth: 80, margin: 0 }}
        size={['100%', 5]}
      />
    </Flex>
  )
}

const acceptedCell = (val: number, row: CardVerification) => {
  const total = val + row.verificationRejected
  const rate = total > 0 ? (val / total) * 100 : 0
  return (
    <Flex align="center" gap={8}>
      <CheckCircleOutlined
        style={{ color: '#22c55e', fontSize: 13, flexShrink: 0 }}
      />
      <span style={{ fontFamily: 'monospace', width: 28 }}>{val}</span>
      {total > 0 && (
        <Tag
          color="success"
          style={{ fontSize: 10, fontFamily: 'monospace', margin: 0 }}
        >
          {rate.toFixed(0)}%
        </Tag>
      )}
    </Flex>
  )
}

const rejectedCell = (val: number, row: CardVerification) => {
  const total = val + row.verificationApprove
  const rate = total > 0 ? (val / total) * 100 : 0
  const tagColor =
    rate === 0 ? 'default' : rate > 30 ? 'error' : rate > 15 ? 'warning' : 'default'
  return (
    <Flex align="center" gap={8}>
      <CloseCircleOutlined
        style={{
          color: val === 0 ? '#545c6e' : '#ef4444',
          fontSize: 13,
          flexShrink: 0,
        }}
      />
      <span style={{ fontFamily: 'monospace', width: 28 }}>{val}</span>
      {total > 0 && val > 0 && (
        <Tag
          color={tagColor}
          style={{ fontSize: 10, fontFamily: 'monospace', margin: 0 }}
        >
          {rate.toFixed(0)}%
        </Tag>
      )}
    </Flex>
  )
}

const nameCell = (val: string) => (
  <Text strong style={{ fontSize: 12 }}>
    {val}
  </Text>
)

const SummaryCards: React.FC<{
  kpiList: CardVerification[]
  totalApprove: number
  totalRejected: number
}> = ({ kpiList, totalApprove, totalRejected }) => {
  if (!kpiList.length) return null

  const totalProcessed = totalApprove + totalRejected
  const acceptRate =
    totalProcessed > 0 ? ((totalApprove / totalProcessed) * 100).toFixed(1) : '0'
  const rejectRate =
    totalProcessed > 0 ? ((totalRejected / totalProcessed) * 100).toFixed(1) : '0'

  return (
    <Flex gap={12} style={{ marginBottom: 16 }} wrap="wrap">
      <Card size="small" style={{ flex: 1, minWidth: 140 }}>
        <Statistic
          title={
            <Tooltip title="Общее количество обработанных заявок на верификацию карт (принятые + отклонённые)">
              <FileProtectOutlined style={{ color: '#3b82f6' }} /> Total
              Processed
            </Tooltip>
          }
          value={totalProcessed}
          valueStyle={{ fontSize: 22, fontFamily: 'monospace' }}
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 140 }}>
        <Statistic
          title={
            <Tooltip title="Количество принятых заявок на верификацию и их доля от общего числа">
              <CheckCircleOutlined style={{ color: '#22c55e' }} /> Accepted
            </Tooltip>
          }
          value={totalApprove}
          valueStyle={{
            fontSize: 22,
            fontFamily: 'monospace',
            color: '#22c55e',
          }}
          suffix={
            <Tag color="success" style={{ fontSize: 10 }}>
              {acceptRate}%
            </Tag>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 140 }}>
        <Statistic
          title={
            <Tooltip title="Количество отклонённых заявок на верификацию и их доля от общего числа">
              <CloseCircleOutlined style={{ color: '#ef4444' }} /> Rejected
            </Tooltip>
          }
          value={totalRejected}
          valueStyle={{
            fontSize: 22,
            fontFamily: 'monospace',
            color: '#ef4444',
          }}
          suffix={
            <Tag color="error" style={{ fontSize: 10 }}>
              {rejectRate}%
            </Tag>
          }
        />
      </Card>
      <Card size="small" style={{ flex: 1, minWidth: 140 }}>
        <Statistic
          title={
            <Tooltip title="Количество менеджеров, обработавших хотя бы одну заявку за период">
              <TeamOutlined style={{ color: '#a855f7' }} /> Managers
            </Tooltip>
          }
          value={kpiList.length}
          valueStyle={{ fontSize: 22, fontFamily: 'monospace' }}
        />
      </Card>
    </Flex>
  )
}

export const CardVerificationsList: React.FC = () => {
  const { kpiList, loading, onChangeKPIList, totalCountOfKPI, totalKPI } =
    useKPIList()
  const userProfile = useUnit($userProfile)
  const hasFullAccess = useMemo(
    () =>
      userProfile.roles.includes(rolesObj.ceo) ||
      userProfile.roles.includes(rolesObj.cfo),
    [userProfile],
  )

  const [filterUserIds, setFilterUserIds] = useState<string[] | undefined>()
  const [datePeriod, setDatePeriod] = useState<RangePickerProps['value']>(() => [
    dayjs().startOf('month'),
    dayjs(),
  ])
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    }
  }

  useEffect(() => {
    if (datePeriod?.[0] && datePeriod?.[1]) {
      const userIds = hasFullAccess ? filterUserIds : [userProfile.id]
      onChangeKPIList({
        userIds,
        beginPeriodAt: datePeriod[0].format('YYYY-MM-DD'),
        endPeriodAt: datePeriod[1].format('YYYY-MM-DD'),
        limit: pageSize,
        page,
      })
    }
  }, [hasFullAccess, userProfile.id, filterUserIds, datePeriod, pageSize, page])

  const handleFiltersChange = ({
    userIds,
    datePeriod: nextDatePeriod,
  }: {
    userIds?: string[]
    datePeriod?: RangePickerProps['value']
  }) => {
    setDatePeriod(nextDatePeriod)
    setFilterUserIds(userIds)
    setPage(1)
  }

  const columns: ColumnsType<CardVerification> = [
    {
      title: 'Manager',
      dataIndex: 'username',
      fixed: 'left',
      width: 160,
      sorter: (a, b) => a.username.localeCompare(b.username),
      defaultSortOrder: 'ascend',
      render: nameCell,
    },
    {
      title: (
        <Tooltip title="Общее количество обработанных заявок менеджером (принятые + отклонённые)">
          Total processed
        </Tooltip>
      ),
      key: 'total',
      width: 130,
      align: 'right',
      sorter: (a, b) =>
        a.verificationApprove +
        a.verificationRejected -
        (b.verificationApprove + b.verificationRejected),
      render: (_: unknown, row: CardVerification) => (
        <span style={{ fontFamily: 'monospace' }}>
          {row.verificationApprove + row.verificationRejected}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="Доля менеджера в общем количестве обработанных заявок на верификацию карт">
          Share %
        </Tooltip>
      ),
      dataIndex: 'verificationExecPercent',
      width: 240,
      sorter: (a, b) => a.verificationExecPercent - b.verificationExecPercent,
      defaultSortOrder: 'descend',
      render: pctCell,
    },
    {
      title: (
        <Tooltip title="Количество принятых заявок и процент принятия от общего числа заявок менеджера">
          Accepted
        </Tooltip>
      ),
      dataIndex: 'verificationApprove',
      width: 160,
      sorter: (a, b) => a.verificationApprove - b.verificationApprove,
      render: acceptedCell,
    },
    {
      title: (
        <Tooltip title="Количество отклонённых заявок и процент отклонения от общего числа заявок менеджера">
          Rejected
        </Tooltip>
      ),
      dataIndex: 'verificationRejected',
      width: 160,
      sorter: (a, b) => a.verificationRejected - b.verificationRejected,
      render: rejectedCell,
    },
  ]

  const renderSummary = () => {
    if (!totalKPI) return null
    const totalProcessed =
      totalKPI.totalVerificationApprove + totalKPI.totalVerificationRejected
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>
          <Text
            strong
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Total
          </Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <Text strong style={{ fontFamily: 'monospace' }}>
            {totalProcessed}
          </Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2}>
          <Text strong style={{ fontFamily: 'monospace' }}>
            {totalKPI.totalVerificationExecPercent.toFixed(2)}%
          </Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3}>
          <Flex align="center" gap={8}>
            <CheckCircleOutlined style={{ color: '#22c55e', fontSize: 13 }} />
            <Text strong style={{ fontFamily: 'monospace' }}>
              {totalKPI.totalVerificationApprove}
            </Text>
          </Flex>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4}>
          <Flex align="center" gap={8}>
            <CloseCircleOutlined style={{ color: '#ef4444', fontSize: 13 }} />
            <Text strong style={{ fontFamily: 'monospace' }}>
              {totalKPI.totalVerificationRejected}
            </Text>
          </Flex>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    )
  }

  return (
    <CCardBody>
      <CardVerificationFilters
        classname="mb-3"
        value={{ userIds: filterUserIds, datePeriod }}
        onChange={handleFiltersChange}
        hasFullAccess={hasFullAccess}
      />

      <SummaryCards
        kpiList={kpiList}
        totalApprove={totalKPI?.totalVerificationApprove ?? 0}
        totalRejected={totalKPI?.totalVerificationRejected ?? 0}
      />

      <Table
        dataSource={kpiList}
        bordered
        size="small"
        scroll={{ x: 700 }}
        loading={loading}
        rowKey="userId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          pageSize: pageSize,
          total: totalCountOfKPI,
        }}
        summary={renderSummary}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current,
            //@ts-ignore
            currentPageSize: pagination.pageSize,
            //@ts-ignore
            currentOrderDirection: sorter.order,
            //@ts-ignore
            currentOrderBy: sorter.field,
          })
        }
      />

      <Flex gap={16} style={{ marginTop: 12 }} align="center" wrap="wrap">
        <Text
          type="secondary"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}
        >
          Rejection rate:
        </Text>
        <Tag color="default">0%</Tag>
        <Tag color="default">low ≤ 15%</Tag>
        <Tag color="warning">mid 15–30%</Tag>
        <Tag color="error">high &gt; 30%</Tag>
      </Flex>
    </CCardBody>
  )
}
